import onetime from "@-/com.npmjs.onetime";
import type { AnalyticsProperties, CapturedAnalyticsEvent } from "#analytics-event";

/** The slice of the PostHog client analytics needs. `lib/posthog` connects the real client. */
export type AnalyticsSink = {
  capture: (event: string, properties: AnalyticsProperties, options: { timestamp: Date }) => void;
  getDistinctId: () => string;
  register: (properties: AnalyticsProperties) => Promise<void>;
  setPersonProperties: (
    set: AnalyticsProperties,
    setOnce: AnalyticsProperties | undefined,
    reloadFeatureFlags: boolean,
  ) => void;
};

/** Events captured before the client exists (module-load or early-render captures). */
const PENDING_EVENT_LIMIT = 200;

type SinkState = {
  pendingEvents: CapturedAnalyticsEvent[];
  pendingPersonProperties: { set: AnalyticsProperties; setOnce: AnalyticsProperties }[];
  pendingSuperProperties: AnalyticsProperties;
  sink: AnalyticsSink | null;
};

const getSinkState = onetime(
  (): SinkState => ({
    pendingEvents: [],
    pendingPersonProperties: [],
    pendingSuperProperties: {},
    sink: null,
  }),
);

/**
 * Connects the analytics client. Anything captured before this point is replayed with its original
 * timestamp, so an early event is neither lost nor misdated.
 */
export function connectAnalyticsSink(sink: AnalyticsSink): void {
  const state = getSinkState();
  state.sink = sink;
  if (Object.keys(state.pendingSuperProperties).length > 0) {
    void sink.register(state.pendingSuperProperties);
    state.pendingSuperProperties = {};
  }
  for (const { set, setOnce } of state.pendingPersonProperties.splice(0)) {
    sink.setPersonProperties(set, setOnce, false);
  }
  for (const event of state.pendingEvents.splice(0)) {
    sink.capture(event.name, event.properties, { timestamp: event.timestamp });
  }
}

export function getAnalyticsSink(): AnalyticsSink | null {
  return getSinkState().sink;
}

export function deliverToAnalyticsSink(event: CapturedAnalyticsEvent): void {
  const state = getSinkState();
  if (state.sink === null) {
    if (state.pendingEvents.length >= PENDING_EVENT_LIMIT) {
      state.pendingEvents.shift();
    }
    state.pendingEvents.push(event);
    return;
  }
  state.sink.capture(event.name, event.properties, { timestamp: event.timestamp });
}

/**
 * Super properties ride on every later event, including the SDK's own `$screen` and lifecycle
 * events, so retention and funnels can be broken down by them.
 */
export function registerAnalyticsSuperProperties(properties: AnalyticsProperties): void {
  const state = getSinkState();
  if (state.sink === null) {
    state.pendingSuperProperties = { ...state.pendingSuperProperties, ...properties };
    return;
  }
  void state.sink.register(properties);
}

/** Never reloads feature flags: a property update must not trigger a flag request. */
export function setAnalyticsPersonProperties(
  set: AnalyticsProperties,
  setOnce: AnalyticsProperties = {},
): void {
  const state = getSinkState();
  if (state.sink === null) {
    state.pendingPersonProperties.push({ set, setOnce });
    return;
  }
  state.sink.setPersonProperties(set, setOnce, false);
}
